import type { MailboxForwardingSettings } from "../types";

export type MailboxRoutingState =
  | "active"
  | "verification_pending"
  | "activation_ready"
  | "disabled"
  | "inactive";

export type UnchangedMailboxAction = "check_verification" | "activate" | null;

export function normalizeMailboxDestination(email: string): string {
  return email.trim();
}

export function validateMailboxDestination(
  email: string,
  forwardingAddress = ""
): string {
  const normalized = normalizeMailboxDestination(email);
  if (!normalized) return "Enter a forwarding destination email.";
  if (normalized.length > 90) {
    return "The destination email must be 90 characters or fewer.";
  }
  if (/\s/.test(normalized)) return "Email addresses cannot contain spaces.";
  if (
    forwardingAddress &&
    normalized.toLowerCase() ===
      normalizeMailboxDestination(forwardingAddress).toLowerCase()
  ) {
    return "The destination must be different from the public mailbox.";
  }

  const atIndex = normalized.lastIndexOf("@");
  if (atIndex <= 0 || atIndex === normalized.length - 1) {
    return "Enter a valid email address.";
  }

  const local = normalized.slice(0, atIndex);
  const domain = normalized.slice(atIndex + 1);
  if (
    local.length > 64 ||
    local.startsWith(".") ||
    local.endsWith(".") ||
    local.includes("..") ||
    !/^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+$/.test(local)
  ) {
    return "Enter a valid email address.";
  }
  if (
    domain.length > 253 ||
    !domain.includes(".") ||
    domain.startsWith(".") ||
    domain.endsWith(".") ||
    domain.includes("..") ||
    !/^[A-Za-z0-9.-]+$/.test(domain) ||
    domain.split(".").some((label) => {
      return (
        !label ||
        label.length > 63 ||
        label.startsWith("-") ||
        label.endsWith("-")
      );
    })
  ) {
    return "Enter a valid email address.";
  }
  return "";
}

export function mailboxRoutingState(
  settings: MailboxForwardingSettings
): MailboxRoutingState {
  const statuses = [settings.status, settings.ruleStatus]
    .filter((status): status is string => typeof status === "string")
    .map((status) => status.toLowerCase().replace(/[_\s-]/g, ""));
  if (
    settings.pendingActivation ||
    statuses.includes("pendingactivation")
  ) {
    return "activation_ready";
  }
  if (
    settings.pendingVerification ||
    statuses.includes("pendingverification") ||
    statuses.includes("destinationunverified") ||
    statuses.includes("pending")
  ) {
    return "verification_pending";
  }
  if (statuses.includes("disabled")) {
    return "disabled";
  }
  if (
    settings.active ||
    settings.success ||
    statuses.includes("active") ||
    (settings.enabled === true && settings.configured !== false) ||
    (settings.configured === true && settings.enabled !== false)
  ) {
    return "active";
  }
  return "inactive";
}

export function pendingMailboxDestination(
  settings: MailboxForwardingSettings
): string {
  return normalizeMailboxDestination(
      settings.pendingDestinationEmail ||
      settings.requestedDestinationEmail ||
      (["verification_pending", "activation_ready"].includes(
        mailboxRoutingState(settings)
      )
        ? settings.destinationEmail || ""
        : "")
  );
}

export function activeMailboxDestination(
  settings: MailboxForwardingSettings
): string {
  if (settings.activeDestinationEmail) {
    return normalizeMailboxDestination(settings.activeDestinationEmail);
  }
  const destination = normalizeMailboxDestination(
    settings.destinationEmail || ""
  );
  const state = mailboxRoutingState(settings);
  if (state === "active") return destination;
  if (
    ["verification_pending", "activation_ready"].includes(state) &&
    settings.enabled === true
  ) {
    const pending = normalizeMailboxDestination(
      settings.pendingDestinationEmail ||
        settings.requestedDestinationEmail ||
        ""
    );
    if (pending && destination.toLowerCase() !== pending.toLowerCase()) {
      return destination;
    }
  }
  return "";
}

export function isVerifiedMailboxDestination(
  settings: MailboxForwardingSettings,
  email: string
): boolean {
  const normalized = normalizeMailboxDestination(email).toLowerCase();
  return Boolean(
    normalized &&
      (settings.destinations?.some(
        (destination) =>
          destination.verified &&
          normalizeMailboxDestination(destination.email).toLowerCase() ===
            normalized
      ) ||
        settings.verifiedDestinationAddresses?.some(
          (destination) =>
            normalizeMailboxDestination(destination).toLowerCase() ===
            normalized
        ))
  );
}

export function unchangedMailboxAction(
  settings: MailboxForwardingSettings,
  email: string
): UnchangedMailboxAction {
  const normalized = normalizeMailboxDestination(email).toLowerCase();
  if (!normalized) return null;
  const state = mailboxRoutingState(settings);

  if (state === "verification_pending") {
    return pendingMailboxDestination(settings).toLowerCase() === normalized
      ? "check_verification"
      : null;
  }
  if (state === "activation_ready") {
    return pendingMailboxDestination(settings).toLowerCase() === normalized
      ? "activate"
      : null;
  }
  if (state === "disabled") {
    const configured = normalizeMailboxDestination(
      settings.destinationEmail || ""
    ).toLowerCase();
    return configured === normalized &&
      isVerifiedMailboxDestination(settings, normalized)
      ? "activate"
      : null;
  }
  return null;
}
